/**
 * Starter Toast — thin wrapper around `@mantine/notifications` so the
 * app calls a single design-system API for transient messages.
 *
 * Usage:
 *   import { toast } from '~/components/ui';
 *   toast.success('Saved', 'Your changes have been saved.');
 *   toast.error('Save failed', 'Please try again.');
 *
 * Surface lives on `--bg-surface` with `--shadow-3` elevation and a 32px
 * variant-coloured icon tile to the left of the title + 12px description.
 * Matches the design's preview/comp-toasts.html.
 *
 * Mount `<Notifications />` once at the root (already wired in __root.tsx).
 */
import type { ReactNode } from 'react';
import {
  notifications,
  type NotificationData,
} from '@mantine/notifications';

export type ToastVariant = 'info' | 'success' | 'warning' | 'error';

interface ShowToastOptions extends Omit<NotificationData, 'message' | 'title' | 'color'> {
  readonly description?: ReactNode;
  /** Optional icon — typically a Lucide icon component. */
  readonly icon?: ReactNode;
  /** Auto-close timeout in ms (default 5000; pass `false` to stay open). */
  readonly autoClose?: number | false;
}

// Maps to the re-shaded Mantine status tuples in theme.ts (success = ledger
// credit-green, matching the Alert/Tag success tone).
const VARIANT_COLOR: Record<ToastVariant, string> = {
  info: 'blue',
  success: 'green',
  warning: 'yellow',
  error: 'red',
};

function showToast(
  variant: ToastVariant,
  title: ReactNode,
  options: ShowToastOptions = {},
): string {
  const { description, icon, autoClose, ...rest } = options;
  return notifications.show({
    title,
    message: description ?? '',
    color: VARIANT_COLOR[variant],
    autoClose: autoClose ?? 5000,
    ...(icon !== undefined ? { icon } : {}),
    ...rest,
  });
}

/**
 * Toast namespace — one function per variant plus `dismiss` and `update`.
 * Each returns the notification id for use with `toast.dismiss(id)`.
 */
export const toast = {
  info: (title: ReactNode, options?: ShowToastOptions) =>
    showToast('info', title, options),
  success: (title: ReactNode, options?: ShowToastOptions) =>
    showToast('success', title, options),
  warning: (title: ReactNode, options?: ShowToastOptions) =>
    showToast('warning', title, options),
  error: (title: ReactNode, options?: ShowToastOptions) =>
    showToast('error', title, options),
  dismiss: (id?: string) =>
    id !== undefined ? notifications.hide(id) : notifications.clean(),
  update: (id: string, data: NotificationData) =>
    notifications.update({ id, ...data }),
};
